import { useState, useEffect } from 'react';
import { formService, questionnaireService } from '@/services';
import useAuth from '@/hooks/stores/useAuth';
import { settingsService } from '@/services';
import UnsavedChangesAlert from '@/pages/Users/Dynamic_Form_parent/UnsavedChangesAlert';
import BreakSuggestionModal from '@/components/BreakSuggestionModal';
import SkippableInput from '@/components/SkippableInput';

export function InitialQuestionnaireDisplay({ 
  setLastSaveTime, 
  setHasUnsavedChanges, 
  debouncedSaveStepData,
  onContinue,
  onBreak,
  showBreakModal,
  setShowBreakModal,
  lastBreakTime,
  setLastBreakTime,
  isSaving,
  setIsSaving,
  hasUnsavedChanges
}) {
  const { userId } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(7000);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [skippedFields, setSkippedFields] = useState({});

  useEffect(() => {
    const fetchQuestionsAndAnswers = async () => {
      try {
        setIsLoading(true);
        
        // Fetch questions using the new API endpoint
        const data = await formService.getInitialFormSubmission(2);
        console.log('this is the form data for the initial questionnaire', data);
        const formSteps = data.data.form_form_steps || [];

        const extractedQuestions = formSteps.flatMap(step => {
          return step.form_step_form_sections.flatMap(section =>
            section.form_step_form_fields.map(field => ({
              id: field.id,
              text: field.name,
              description: field.description || '',
              fieldType: field.field_type,
              sectionId: section.id,
              stepId: step.id
            }))
          );
        });

        setQuestions(extractedQuestions);

        // Fetch user's answers using the new legacy API endpoint
        const responseData = await questionnaireService.getLegacyInitialUserData(userId);

        if (responseData.success && responseData.data.form_submissions?.[0]) {
          const submissionValues = responseData.data.form_submissions[0].submission_values;
          const answersObj = {};
          const newSkippedFields = {};
          
          submissionValues.forEach(submission => {
            answersObj[submission.field_id] = submission.value;
            // Initialize skipped fields from submission values
            const key = `${submission.section}_${submission.field_id}_0`;
            if (submission.skip === 1) {
              newSkippedFields[key] = true;
            }
          });
          
          setAnswers(answersObj);
          setSkippedFields(newSkippedFields);
        }

      } catch (err) {
        console.error("Failed to fetch questions and answers", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestionsAndAnswers();
  }, [userId]);

  useEffect(() => {
    const initializeAutoSave = async () => {
      const timeout = await settingsService.getAutoSaveTimeout();
      setAutoSaveTimeout(timeout);
    };

    initializeAutoSave();
  }, []);

  // Update handleAnswerChange to use parent's save functionality
  const handleAnswerChange = (questionId, sectionId, newValue) => {
    setAnswers(prev => ({ ...prev, [questionId]: newValue }));
    setHasUnsavedChanges(true);

    // Use the parent's debounced save function
    debouncedSaveStepData(
      async () => {
        try {
        const question = questions.find(q => q.id === questionId);
        const payload = {
          form_id: 2,
          step_id: question.stepId,
          user_id: userId,
          values: [
            {
              field_id: questionId,
              section: sectionId,
              section_repeat_index: 0,
              value: newValue
            }
          ],
          removed_sections: []
        };

          console.log('Saving answer with payload:', payload);
          const response = await questionnaireService.saveStepData(payload);
          
          if (response.status !== "success") {
            console.error('Server returned unsuccessful response:', response);
            throw new Error(response.message || 'Server returned unsuccessful response');
        }

        // Update last save time using parent's function
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        setHasUnsavedChanges(false);
          return response;
        } catch (error) {
          console.error('Error saving answer:', error);
          // Re-throw the error with more context
          throw new Error(`Failed to save answer: ${error.message}`);
        }
      }
    );
  };

  // Add function to handle skip status changes
  const handleSkipChange = async (questionId, sectionId, isSkipped) => {
    try {
      setIsSaving(true);
      
      // Get current answer value
      const currentValue = answers[questionId] || "";
      
      // Create payload for the API
      const payload = {
        form_id: 2,
        step_id: questions.find(q => q.id === questionId)?.stepId,
        user_id: userId,
        values: [{
          field_id: Number(questionId),
          section: Number(sectionId),
          section_repeat_index: 0,
          value: currentValue,
          skip: isSkipped ? 1 : 0
        }]
      };

      console.log('Saving skip status with payload:', payload);
      const response = await questionnaireService.saveStepData(payload);
      
      if (response.status === "success") {
        // Update local state to track skipped status
        setSkippedFields(prev => ({
          ...prev,
          [`${sectionId}_${questionId}_0`]: isSkipped
        }));

        // Update last save time
        const time = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        setLastSaveTime(time);
        setHasUnsavedChanges(false);
      } else {
        throw new Error(response.message || 'Failed to update skip status');
      }
    } catch (error) {
      console.error("Error updating skip status:", error);
      // You might want to show a toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  // Update handleLocalSaveAndNext to handle breaks properly
  const handleLocalSaveAndNext = async () => {
    try {
      setIsSaving(true);
      
      // Save all answers
      const savePromises = questions.map(question => {
        const answer = answers[question.id];
        if (answer) {
          return debouncedSaveStepData(async () => {
            const payload = {
              form_id: 2,
              step_id: question.stepId,
              user_id: userId,
              values: [
                {
                  field_id: question.id,
                  section: question.sectionId,
                  section_repeat_index: 0,
                  value: answer
                }
              ],
              removed_sections: []
            };

            const response = await questionnaireService.saveStepData(payload);

            if (!response.success) {
              throw new Error('Failed to save answer');
            }

            return response.data;
          });
        }
        return Promise.resolve();
      });

      await Promise.all(savePromises.filter(Boolean));

      // Update last save time
      const time = new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      if (typeof setLastSaveTime === 'function') {
        setLastSaveTime(time);
      }
      setHasUnsavedChanges(false);

      // Check if 1 minute has passed since last break
      const currentTime = Date.now();
      const oneMinuteInMs = 5 * 60 * 1000; // 5 minute
      const timeSinceLastBreak = currentTime - (lastBreakTime || 0);
      // console.log('Time since last break (seconds):', Math.floor(timeSinceLastBreak / 1000));

      if (isFirstVisit || timeSinceLastBreak >= oneMinuteInMs) {
        // console.log('Showing break modal');
        if (typeof setShowBreakModal === 'function') {
          setShowBreakModal(true);
          if (typeof setLastBreakTime === 'function') {
            setLastBreakTime(currentTime);
          }
          setIsFirstVisit(false); // Set first visit to false after showing modal
        } else {
          console.warn('setShowBreakModal is not available');
          onContinue(); // Fallback to continue if modal functionality isn't available
        }
      } else {
        // console.log('Not showing break modal, continuing');
        onContinue();
      }
    } catch (error) {
      console.error("Error saving questionnaire data:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Add effect to handle break modal actions
  useEffect(() => {
    if (!showBreakModal) return;

    const handleBreakModalAction = (takeBreak) => {
      setShowBreakModal(false);
      if (takeBreak) {
        onBreak();
      } else {
        onContinue();
      }
    };

    // Add event listeners for break modal actions if needed
    return () => {
      // Cleanup if needed
    };
  }, [showBreakModal, onBreak, onContinue]);

  if (isLoading) {
    return (
      <div className="bg-[#F1EBDB] rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F16745]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <UnsavedChangesAlert hasUnsavedChanges={hasUnsavedChanges} />
      {typeof showBreakModal === 'boolean' && (
        <BreakSuggestionModal
          isOpen={showBreakModal}
          onTakeBreak={() => {
            if (typeof setShowBreakModal === 'function') {
              setShowBreakModal(false);
            }
            onBreak();
          }}
          onContinue={() => {
            if (typeof setShowBreakModal === 'function') {
              setShowBreakModal(false);
            }
            onContinue();
          }}
        />
      )}
      <h2 className="text-2xl font-bold text-[#F16745] mb-8">Initial Questionnaire Responses</h2>
      
      <div className="space-y-8">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b border-gray-200 pb-6 last:border-b-0">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 max-w-2xl">
                <label className="block font-medium text-gray-700 w-auto">
                  {index + 1}. {question.text}
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
              </div>
              {question.description && (
                <p className="text-sm text-gray-600 mb-2">{question.description}</p>
              )}
              <div className="mt-2">
                <SkippableInput
                  type="textarea"
                  rows={4}
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question.id, question.sectionId, e.target.value)}
                  className="min-h-[100px]"
                  field={question}
                  sectionId={question.sectionId}
                  index={0}
                  isSkipped={skippedFields[`${question.sectionId}_${question.id}_0`]}
                  onSkipChange={(isSkipped) => handleSkipChange(question.id, question.sectionId, isSkipped)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Save & Next Button */}
      <div className="mt-8 flex justify-end pt-6">
        <button
          type="button"
          onClick={handleLocalSaveAndNext}
          className="ml-2 bg-[#F16745] px-6 py-2.5 text-sm font-medium rounded-lg text-white hover:bg-[#ee623f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
          disabled={isSaving}
        >
          {isSaving ? (
            <span className="flex items-center">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              Saving...
            </span>
          ) : (
            "Save & Next →"
          )}
        </button>
      </div>
    </div>
  );
} 